







































import {
    SdsButton,
    SdsIcon,
    SdsAlert,
    SdsInputPhone,
    SanitizedHtml,
} from '@superbet-group/web.lib.ui-components';
import {
    respondToBelow,
    ScreenWidth,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import {
    computed,
    defineComponent,
    ref,
    watch,
} from '@vue/composition-api';
import countryStoreIndex from '@store/modules/data/country/countryIndex';
import modalStore from '@store/modules/ui/shared/modal/modalIndex';
import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { errorGuard } from '@core/utils/ErrorGuard';
import { UserService } from '@core/services/user/UserService';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import errorIndex from '@store/modules/errors/errorIndex';
import SmsLimitReachedError from '@core/errors/user/SmsLimitReachedError';
import { ValidationError } from '@superbet-group/web.lib.utilities';
import { captiveKycStore } from '@accountFederation/store/data/captiveKyc/captiveKycStore';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { usePhoneParser } from '@accountFederation/modules/settings/usePhoneParser';
import { isoCode2ToIsoCode } from '@sportOfferFederation/flags/flagsDefinitions';
import { getConfig } from '@superbet-group/web.lib.config';
import Country from '@core/models/country/Country';

export default defineComponent({
    name: 'PhoneVerificationCaptive',
    setup() {
        const countryStore = useVuexStore<typeof countryStoreIndex>('data/country');
        const fetchCountries = countryStore.action('fetchCountries');
        const countries = countryStore.getter('countries');
        fetchCountries();

        const {
            parsePhoneNumber,
            prefix: phonePrefix,
            localNumber: phoneNumber,
            rawFullPhoneNumber,
        } = usePhoneParser(countries);

        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const showSessionExpired = authModule.action('sessionExpired');
        const setUserData = authModule.action('setUserData');
        const user = authModule.getter('user');

        const errorMessage = ref('');

        const isLoading = ref(false);

        const modalModule = useVuexStore<typeof modalStore>('ui/modal');
        const setModal = modalModule.action('setModal');
        const pushModal = modalModule.action('pushModal');

        const errorModule = useVuexStore<typeof errorIndex>('errors');
        const showError = errorModule.action('showError');

        const captiveKycModule = useVuexStore<typeof captiveKycStore>('data/captiveKyc');
        const freeFromCaptiveKyc = captiveKycModule.action('freeFromCaptiveKyc');

        AnalyticsSDK.getInstance().log('VerifyMobile_Start');

        // eslint-disable-next-line sonarjs/cognitive-complexity
        async function sendCode() {
            AnalyticsSDK.getInstance().log('VerifyMobile_Intent');
            errorMessage.value = '';
            await errorGuard(async () => {
                isLoading.value = true;
                try {
                    if (rawFullPhoneNumber.value !== user.value?.phone) {
                        await UserService.getInstance().updatePhone(rawFullPhoneNumber.value!);
                    } else {
                        await UserService.getInstance().sendSmsCode();
                    }

                    setModal({
                        code: modalCodes.smsVerify,
                        data: {
                            phone: rawFullPhoneNumber.value,
                            hasFooter: true,
                            isInCaptiveKyc: true,
                        },
                    });

                    setUserData({
                        key: 'phone',
                        value: rawFullPhoneNumber.value,
                    });
                } catch (error) {
                    if ((error as any).type === 'authenticationNeeded') {
                        freeFromCaptiveKyc({
                            shouldRedirect: true,
                        });
                        showSessionExpired();
                    }

                    if (error instanceof SmsLimitReachedError) {
                        pushModal({
                            isClosable: false,
                            code: respondToBelow(ScreenWidth.tSm) ? modalCodes.mobileStatusCode : modalCodes.statusCode,
                            ...(!respondToBelow(ScreenWidth.tSm) && { title: 'sms limit reached' }),
                            data: {
                                ...(respondToBelow(ScreenWidth.tSm) && { title: 'sms limit reached' }),
                                text: 'You have sent the maximum number of SMS messages. If you wish to send more, please contact support',
                                illustrationName: 'verification-sms-warning',
                            },
                        });
                    } else if (error instanceof ValidationError) {
                        errorMessage.value = (error.fieldErrors.phone || error.fieldErrors.general || ['Something went wrong. Please try again'])[0];
                    } else {
                        errorMessage.value = 'Something went wrong. Please try again';
                    }
                } finally {
                    isLoading.value = false;
                }
            }, {
                onError: (e) => {
                    showError(e);
                },
            });
        }

        const sendPhoneNumberChangedEvent = () => {
            AnalyticsSDK.getInstance().log('VerifyMobile_Input_Mobile');
        };

        watch(() => countries.value, () => {
            try {
                if (user.value?.phone) {
                    parsePhoneNumber(user.value.phone);
                }
            } catch (e) {
                errorMessage.value = 'Something went wrong. Please try again';
            }
        }, { immediate: true });

        const countryFlag = computed(() => {
            return countries.value.find((country: Country) => country.prefix === phonePrefix.value)?.code
                || getConfig().countrySpecific.country.slice(0, 2).toUpperCase();
        });

        const prefixOption = computed(() => ([{
            value: phonePrefix.value,
            name: phonePrefix.value,
            flag: isoCode2ToIsoCode[countryFlag.value],
        }]));

        return {
            errorMessage,
            phoneNumber,
            phonePrefix,
            options: prefixOption,
            isLoading,
            hasInitialPhoneNumber: user.value?.phone,
            sendCode,
            sendPhoneNumberChangedEvent,
        };
    },
    components: {
        SdsButton,
        SdsIcon,
        SdsInputPhone,
        SdsAlert,
        SanitizedHtml,
    },
});
